import { createSignal, For, onCleanup, onMount, Show } from "solid-js";
import { TaskQueueSchedulersPanel } from "./TaskQueueSchedulersPanel";
import { QueueSectionHeading } from "./TaskQueueUi";
import { loadTaskQueueMonitoringMode, useTaskQueueMonitor, type TaskQueueMonitoringMode } from "../hooks/useTaskQueueMonitor";
import {
  api,
  type CoverageProcessingSettings,
  type TaskQueueDefinitionSettings,
  type TaskQueueExecutionSettings,
  type TaskQueueEvent,
  type TaskQueueJob,
  type TaskQueueJobDetail,
  type TaskQueueJobStatus,
  type TaskQueueSchedulerDelta,
  type TaskQueueStats,
} from "../shared";

const STATUSES: TaskQueueJobStatus[] = [
  "pending", "leased", "retry_wait", "completed", "failed", "dead_letter", "cancel_requested", "cancelled",
];

const PAGE_SIZES = [25, 50, 100, 250];

const STATUS_LABELS: Record<TaskQueueJobStatus, string> = {
  pending: "Pending",
  leased: "Running",
  retry_wait: "Waiting to retry",
  completed: "Completed",
  failed: "Failed",
  dead_letter: "Dead letter",
  cancel_requested: "Cancelling",
  cancelled: "Cancelled",
};

function coverageTaskMeta(task: TaskQueueJob) {
  const payload = task.payload && typeof task.payload === "object"
    ? task.payload as Record<string, unknown>
    : {};
  const blockIds = Array.isArray(payload.blockIds)
    ? payload.blockIds.filter((value): value is string => typeof value === "string")
    : [];
  return {
    generationId: typeof payload.generationId === "string" ? payload.generationId : null,
    blockIds,
    isProjectTask: task.taskType === "coverage.materialize_project.v2",
    blocksPerSlice: Number(payload.blocksPerBatch ?? 0),
  };
}

function taskProgress(task: TaskQueueJob) {
  const progress = task.progress && typeof task.progress === "object"
    ? task.progress as Record<string, unknown>
    : {};
  const completed = Number(progress.completedBlocks ?? 0);
  const total = Number(progress.totalBlocks ?? 0);
  const percent = Math.max(0, Math.min(100, Number(progress.percent ?? (total > 0 ? completed / total * 100 : 0))));
  const phase = typeof progress.phase === "string"
    ? progress.phase.replaceAll("_", " ")
    : task.status === "leased" ? "starting" : task.status === "pending" ? "not started" : task.status;
  return { completed, total, percent, phase };
}

function reconcileById<T extends { id: string }>(current: T[], incoming: T[]): T[] {
  const currentById = new Map(current.map((item) => [item.id, item]));
  return incoming.map((item) => {
    const previous = currentById.get(item.id);
    return previous && JSON.stringify(previous) === JSON.stringify(item) ? previous : item;
  });
}

export function TaskQueuePanel() {
  const [stats, setStats] = createSignal<TaskQueueStats | null>(null);
  const [settings, setSettings] = createSignal<TaskQueueExecutionSettings | null>(null);
  const [coverageSettings, setCoverageSettings] = createSignal<CoverageProcessingSettings | null>(null);
  const [coverageQueueSettings, setCoverageQueueSettings] = createSignal<TaskQueueDefinitionSettings | null>(null);
  const [view, setView] = createSignal<"queue" | "coverage" | "schedulers">(
    location.hash.startsWith("#task-queues/coverage") ? "coverage" : location.hash.startsWith("#task-queues/schedulers") ? "schedulers" : "queue",
  );
  const [monitoringMode, setMonitoringMode] = createSignal<TaskQueueMonitoringMode>(loadTaskQueueMonitoringMode());
  const [schedulerEvent, setSchedulerEvent] = createSignal<TaskQueueEvent | null>(null);
  const [schedulerDelta, setSchedulerDelta] = createSignal<TaskQueueSchedulerDelta | null>(null);
  const [schedulerEventCursor, setSchedulerEventCursor] = createSignal(0);
  const [schedulerBootstrapRevision, setSchedulerBootstrapRevision] = createSignal(0);
  const [latestEventCursor, setLatestEventCursor] = createSignal(0);
  const [loadRevision, setLoadRevision] = createSignal(0);
  const [cursorStack, setCursorStack] = createSignal<Array<string | null>>([null]);
  const [nextCursor, setNextCursor] = createSignal<string | null>(null);
  const [hasNext, setHasNext] = createSignal(false);
  const [newUpdates, setNewUpdates] = createSignal(0);
  const [tasks, setTasks] = createSignal<TaskQueueJob[]>([]);
  const [status, setStatus] = createSignal<"" | TaskQueueJobStatus>("");
  const [queueName, setQueueName] = createSignal("");
  const [selected, setSelected] = createSignal<TaskQueueJobDetail | null>(null);
  const [coverageGenerations, setCoverageGenerations] = createSignal<Array<Record<string, unknown>>>([]);
  const [taskType, setTaskType] = createSignal("");
  const [projectId, setProjectId] = createSignal("");
  const [generationId, setGenerationId] = createSignal("");
  const [from, setFrom] = createSignal("");
  const [to, setTo] = createSignal("");
  const [page, setPage] = createSignal(1);
  const [perPage, setPerPage] = createSignal(25);
  const [failedTaskAgeDays, setFailedTaskAgeDays] = createSignal(1);
  const [terminalDeleteLimit, setTerminalDeleteLimit] = createSignal(100);
  const [terminalDeleteStatuses, setTerminalDeleteStatuses] = createSignal<TaskQueueJobStatus[]>([
    "failed", "dead_letter", "cancelled",
  ]);
  const [taskActionFeedback, setTaskActionFeedback] = createSignal<null | {
    kind: "success" | "error";
    text: string;
  }>(null);
  const [manualRunLimit, setManualRunLimit] = createSignal(10);
  const [total, setTotal] = createSignal(0);
  const [totalPages, setTotalPages] = createSignal(1);
  const [confirmAction, setConfirmAction] = createSignal<null | {
    label: string;
    run: () => Promise<unknown>;
    successMessage?: (result: unknown) => string;
  }>(null);
  const [loading, setLoading] = createSignal(false);
  const [error, setError] = createSignal("");
  const [success, setSuccess] = createSignal("");
  let activeLoad: AbortController | null = null;
  const deletedTaskIds = new Set<string>();

  const currentCursor = () => cursorStack().at(-1) ?? undefined;
  const requestBootstrap = () => setLoadRevision((value) => value + 1);

  function taskFilters() {
    const isCoverage = view() === "coverage";
    return {
      queueName: isCoverage ? "coverage" : queueName() || undefined,
      status: status() || undefined,
      taskType: isCoverage ? undefined : taskType() || undefined,
      projectId: projectId() || undefined,
      generationId: isCoverage ? generationId() || undefined : undefined,
      from: from() ? new Date(from()).toISOString() : undefined,
      to: to() ? new Date(to()).toISOString() : undefined,
    };
  }

  async function load(): Promise<number> {
    activeLoad?.abort();
    const controller = new AbortController();
    activeLoad = controller;
    setLoading(true);
    setError("");
    try {
      if (view() === "schedulers") {
        const nextStats = await api.taskQueue.stats();
        setStats(nextStats);
        setSchedulerEventCursor(nextStats.changeCursor);
        setSchedulerBootstrapRevision((value) => value + 1);
        return nextStats.changeCursor;
      }
      const isCoverage = view() === "coverage";
      const [nextStats, nextSettings, response, generations, nextCoverageSettings, nextQueueSettings] = await Promise.all([
        api.taskQueue.stats(),
        api.taskQueue.settings(),
        api.taskQueue.tasks({
          ...taskFilters(),
          perPage: perPage(),
          cursor: currentCursor(),
          signal: controller.signal,
        }),
        isCoverage ? api.taskQueue.coverageGenerations(projectId() || undefined) : Promise.resolve([]),
        isCoverage ? api.taskQueue.coverageSettings() : Promise.resolve(null),
        isCoverage ? api.taskQueue.queueSettings("coverage") : Promise.resolve(null),
      ]);
      setStats(nextStats);
      setSettings(nextSettings);
      setTasks((current) => reconcileById(
        current,
        response.items.filter((item) => !deletedTaskIds.has(item.id)),
      ));
      setPage(cursorStack().length);
      setPerPage(response.perPage);
      setTotal(response.total);
      setTotalPages(response.totalPages);
      setNextCursor(response.nextCursor ?? null);
      setHasNext(Boolean(response.hasNext));
      setNewUpdates(0);
      setCoverageGenerations((current) => reconcileById(
        current as Array<Record<string, unknown> & { id: string }>,
        generations as Array<Record<string, unknown> & { id: string }>,
      ));
      setCoverageSettings(nextCoverageSettings);
      setCoverageQueueSettings(nextQueueSettings);
      return Math.max(nextStats.changeCursor, response.changeCursor ?? 0);
    } catch (cause: any) {
      if (cause?.name === "AbortError") return stats()?.changeCursor ?? 0;
      setError(cause?.message ?? "Failed to load task queue");
      throw cause;
    } finally {
      if (activeLoad === controller) activeLoad = null;
      setLoading(false);
    }
  }

  async function loadIncremental(after: number) {
    if (view() === "schedulers") {
      const response = await api.taskQueue.schedulerChanges(after);
      if (!response.resyncRequired) setSchedulerDelta(response);
      return response;
    }
    if (cursorStack().length > 1) {
      setNewUpdates((value) => value + 1);
      return { changeCursor: Math.max(after, latestEventCursor()) };
    }
    const response = await api.taskQueue.taskChanges({ after, ...taskFilters() });
    if (response.resyncRequired) return response;
    const removed = new Set(response.removedIds);
    setTasks((current) => {
      const byId = new Map(current.filter((item) => !removed.has(item.id)).map((item) => [item.id, item]));
      for (const item of response.changes) if (!deletedTaskIds.has(item.id)) byId.set(item.id, item);
      return [...byId.values()]
        .sort((left, right) => right.createdAt.localeCompare(left.createdAt) || right.id.localeCompare(left.id))
        .slice(0, perPage());
    });
    if (response.stats) setStats(response.stats);
    if (response.total != null) {
      setTotal(response.total);
      setTotalPages(Math.max(1, Math.ceil(response.total / perPage())));
    }
    if (view() === "coverage" && response.changes.length + response.removedIds.length > 0) {
      const generations = await api.taskQueue.coverageGenerations(projectId() || undefined);
      setCoverageGenerations((current) => reconcileById(
        current as Array<Record<string, unknown> & { id: string }>,
        generations as Array<Record<string, unknown> & { id: string }>,
      ));
    }
    return response;
  }

  const monitor = useTaskQueueMonitor({
    mode: monitoringMode,
    scope: () => `${view()}:${loadRevision()}`,
    onBootstrap: load,
    onIncremental: loadIncremental,
    onEvent: (event) => {
      setLatestEventCursor(event.id);
      if (event.eventType.startsWith("scheduler.") || event.eventType.startsWith("execution.") || event.eventType.startsWith("driver.")) {
        setSchedulerEventCursor(event.id);
        setSchedulerEvent(event);
        return Boolean(event.payload?.monitor && typeof event.payload.monitor === "object");
      }
      // Scheduler monitoring does not render task entities. Acknowledge their
      // durable events directly instead of fetching an irrelevant task delta.
      if (view() === "schedulers") return true;
      return false;
    },
  });

  onMount(() => {
    const syncView = () => {
      setView(location.hash.startsWith("#task-queues/coverage") ? "coverage" : location.hash.startsWith("#task-queues/schedulers") ? "schedulers" : "queue");
      setCursorStack([null]);
      setPage(1);
      requestBootstrap();
    };
    window.addEventListener("hashchange", syncView);
    onCleanup(() => {
      window.removeEventListener("hashchange", syncView);
    });
    onCleanup(() => activeLoad?.abort());
  });

  function requestAction(
    label: string,
    run: () => Promise<unknown>,
    successMessage?: (result: unknown) => string,
  ) {
    setConfirmAction({ label, run, successMessage });
  }

  function requestTerminalTaskCleanup(filters: {
    queueName?: string;
    taskType?: string;
    projectId?: string;
    generationId?: string;
  }) {
    const days = Math.max(0, Math.floor(failedTaskAgeDays()));
    const limit = Math.max(0, Math.min(10_000, Math.floor(terminalDeleteLimit())));
    const statuses = terminalDeleteStatuses();
    if (!statuses.length) {
      setTaskActionFeedback({ kind: "error", text: "Select at least one status to delete." });
      return;
    }
    const olderThan = new Date(Date.now() - days * 86_400_000).toISOString();
    const statusLabels = statuses.map((item) => STATUS_LABELS[item]).join(", ");
    requestAction(
      `Permanently delete ${limit === 0 ? "all matching tasks" : `up to ${limit} task${limit === 1 ? "" : "s"}`} with status ${statusLabels}, ${days === 0 ? "regardless of age" : `older than ${days} day${days === 1 ? "" : "s"}`}? Attempt history will also be deleted.`,
      () => api.taskQueue.deleteTerminal({ olderThan, statuses, limit, ...filters }),
      (result) => {
        const deleted = Number((result as { deleted?: number } | null)?.deleted ?? 0);
        return deleted === 0
          ? "No matching terminal tasks were found."
          : `Deleted ${deleted} terminal task${deleted === 1 ? "" : "s"} and associated attempt history.`;
      },
    );
  }

  function updateSetting(key: keyof TaskQueueExecutionSettings, value: string) {
    const current = settings();
    if (!current) return;
    setSettings({ ...current, [key]: Number(value) });
  }

  function updateCoverageSetting(key: keyof CoverageProcessingSettings, value: string) {
    const current = coverageSettings();
    if (!current) return;
    setCoverageSettings({ ...current, [key]: Number(value) });
  }

  function selectedQueue() {
    return stats()?.queues.find((queue) => queue.name === queueName()) ?? null;
  }

  function monitoredCounts() {
    const current = stats();
    if (!current) return null;
    const name = view() === "coverage" ? "coverage" : queueName();
    return name ? current.queueStatusCounts?.[name] ?? null : current.statusCounts;
  }

  function requestDeleteTask(task: TaskQueueJob) {
    requestAction(
      `Permanently delete task ${task.id}? Its attempt history will also be removed.`,
      async () => {
        const result = await api.taskQueue.delete(task.id);
        deletedTaskIds.add(task.id);
        setTasks((current) => current.filter((item) => item.id !== task.id));
        setTotal((current) => Math.max(0, current - 1));
        if (selected()?.id === task.id) setSelected(null);
        return result;
      },
      () => `Deleted task ${task.id}.`,
    );
  }

  function requestProcessQueue(name: string) {
    const limit = Math.max(1, Math.min(100, Math.floor(manualRunLimit())));
    requestAction(
      `Run up to ${limit} pending task${limit === 1 ? "" : "s"} from "${name}" now? Progress will update while the VPS worker consumes them.`,
      () => api.taskQueue.process(name, limit),
      (result) => {
        const value = result as { claimed?: number; completed?: number; failed?: number };
        return `Worker finished: ${value.claimed ?? 0} claimed, ${value.completed ?? 0} completed, ${value.failed ?? 0} failed.`;
      },
    );
  }

  function toggleTerminalDeleteStatus(status: TaskQueueJobStatus) {
    setTerminalDeleteStatuses((current) =>
      current.includes(status)
        ? current.filter((item) => item !== status)
        : [...current, status],
    );
  }

  function reloadFromFirstPage() {
    setCursorStack([null]);
    setPage(1);
    requestBootstrap();
  }

  function goToNextPage() {
    const cursor = nextCursor();
    if (!cursor) return;
    setCursorStack((current) => [...current, cursor]);
    setPage((value) => value + 1);
    requestBootstrap();
  }

  function goToPreviousPage() {
    if (cursorStack().length <= 1) return;
    setCursorStack((current) => current.slice(0, -1));
    setPage((value) => Math.max(1, value - 1));
    requestBootstrap();
  }

  async function executeConfirmed() {
    const action = confirmAction();
    if (!action) return;
    setConfirmAction(null);
    setLoading(true);
    setError("");
    setSuccess("");
    try {
      const result = await action.run();
      const message = action.successMessage?.(result) ?? "Queue action completed successfully.";
      setSuccess(message);
      setTaskActionFeedback({ kind: "success", text: message });
      requestBootstrap();
    } catch (cause: any) {
      const message = cause?.message ?? "Task queue action failed";
      setError(message);
      setTaskActionFeedback({ kind: "error", text: message });
      setLoading(false);
    }
  }

  return (
    <div class="space-y-6">
      <section class="overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900 via-gray-900 to-cyan-950/20 shadow-xl shadow-black/10">
        <div class="flex flex-wrap items-start justify-between gap-5 p-6">
          <div class="min-w-0">
            <div class="text-xs font-semibold uppercase tracking-[0.18em] text-cyan-500/80">Background operations</div>
            <h2 class="mt-1 text-xl font-semibold tracking-tight text-gray-100">{view() === "coverage" ? "Coverage Processing" : view() === "schedulers" ? "Queue Schedulers" : "Task Queue"}</h2>
            <p class="mt-1 max-w-3xl text-sm leading-6 text-gray-400">
              {view() === "coverage"
                ? "Configure and monitor block-coverage generation independently from queue execution."
                : view() === "schedulers"
                  ? "Configure wake-up schedules and inspect execution activity across VPS and Cloudflare."
                  : "Monitor durable background work, investigate failures, and tune worker behavior."}
            </p>
          </div>
          <div class="flex min-w-[280px] flex-wrap items-center gap-3 rounded-xl border border-gray-700/80 bg-gray-950/55 p-3">
            <span class={`h-2.5 w-2.5 shrink-0 rounded-full ${monitor.state() === "connected" ? "bg-emerald-400" : monitor.state() === "disconnected" ? "bg-amber-400" : "animate-pulse bg-cyan-400"}`} />
            <div class="min-w-32 flex-1"><div class="text-xs font-semibold uppercase tracking-wide text-gray-300">Live monitoring</div><div class="mt-0.5 truncate text-xs text-gray-500">{monitor.message()}</div></div>
            <select value={monitoringMode()} onChange={(event) => setMonitoringMode(event.currentTarget.value as TaskQueueMonitoringMode)} class="h-9 rounded-lg border border-gray-700 bg-gray-950 px-3 text-xs text-gray-200" aria-label="Task queue monitoring mode"><option value="websocket">WebSocket</option><option value="manual">Manual refresh</option><option value="poll-5">Poll every 5s</option><option value="poll-15">Poll every 15s</option><option value="poll-30">Poll every 30s</option><option value="poll-60">Poll every 60s</option></select>
            <button onClick={monitor.retryNow} class="h-9 rounded-lg border border-gray-700 px-3 text-xs font-medium text-gray-200 hover:bg-gray-800">{monitor.state() === "unavailable" ? "Retry now" : "Refresh"}</button>
          </div>
        </div>
        <nav class="flex gap-1 border-t border-gray-800 bg-gray-950/30 p-2" aria-label="Task queue views">
          <button onClick={() => { location.hash = "task-queues"; }} class={`rounded-lg px-4 py-2.5 text-sm font-medium transition ${view() === "queue" ? "bg-cyan-950/70 text-cyan-300 shadow-inner shadow-cyan-900/20" : "text-gray-400 hover:bg-gray-800/70 hover:text-gray-200"}`}>Tasks</button>
          <button onClick={() => { location.hash = "task-queues/coverage"; }} class={`rounded-lg px-4 py-2.5 text-sm font-medium transition ${view() === "coverage" ? "bg-cyan-950/70 text-cyan-300 shadow-inner shadow-cyan-900/20" : "text-gray-400 hover:bg-gray-800/70 hover:text-gray-200"}`}>Coverage</button>
          <button onClick={() => { location.hash = "task-queues/schedulers"; }} class={`rounded-lg px-4 py-2.5 text-sm font-medium transition ${view() === "schedulers" ? "bg-cyan-950/70 text-cyan-300 shadow-inner shadow-cyan-900/20" : "text-gray-400 hover:bg-gray-800/70 hover:text-gray-200"}`}>Schedulers</button>
        </nav>
      </section>

      <Show when={error()}>
        <div class="flex items-center justify-between gap-3 rounded-lg border border-red-800 bg-red-950/60 px-4 py-3 text-sm text-red-200">
          <span>{error()}</span>
          <button onClick={() => setError("")} class="font-semibold text-red-300 hover:text-white">Close</button>
        </div>
      </Show>
      <Show when={monitor.state() === "unavailable"}>
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-800 bg-amber-950/45 px-4 py-3 text-sm text-amber-100">
          <div><div class="font-semibold">Monitoring unavailable after {monitor.attempt()} attempts</div><div class="mt-1 text-xs text-amber-300/80">{monitor.message()} Last successful snapshot: {monitor.lastSnapshotAt() ? new Date(monitor.lastSnapshotAt()!).toLocaleString() : "never"}.</div></div>
          <button onClick={monitor.retryNow} class="rounded-lg bg-amber-600 px-4 py-2 font-semibold text-gray-950 hover:bg-amber-500">Retry now</button>
        </div>
      </Show>
      <Show when={success()}>
        <div class="flex items-center justify-between gap-3 rounded-lg border border-emerald-800 bg-emerald-950/50 px-4 py-3 text-sm text-emerald-200">
          <span>{success()}</span>
          <button onClick={() => setSuccess("")} class="font-semibold text-emerald-300 hover:text-white">Close</button>
        </div>
      </Show>

      <Show when={view() !== "schedulers" ? monitoredCounts() : null}>
        {(counts) => (
          <section class={`rounded-2xl border p-4 ${Number(counts().leased ?? 0) > 0 ? "border-cyan-700 bg-cyan-950/30" : "border-gray-800 bg-gray-900"}`}>
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div class="flex items-center gap-3">
                <span class={`h-3 w-3 rounded-full ${Number(counts().leased ?? 0) > 0 ? "animate-pulse bg-cyan-400" : Number(counts().pending ?? 0) + Number(counts().retry_wait ?? 0) > 0 ? "bg-amber-400" : "bg-gray-600"}`} />
                <div>
                  <div class="font-semibold text-gray-100">{Number(counts().leased ?? 0) > 0 ? "Workers active" : Number(counts().pending ?? 0) + Number(counts().retry_wait ?? 0) > 0 ? "Work waiting" : "Queue idle"}</div>
                  <div class="text-xs text-gray-500">{monitoringMode() === "websocket" ? "Updated from durable queue events." : monitor.message()}</div>
                </div>
              </div>
              <div class="flex flex-wrap gap-4 text-sm text-gray-300">
                <span><b class="text-cyan-300">{counts().leased ?? 0}</b> running</span>
                <span><b class="text-amber-300">{counts().pending ?? 0}</b> pending</span>
                <span><b>{counts().retry_wait ?? 0}</b> retrying</span>
                <span><b class="text-emerald-300">{counts().completed ?? 0}</b> completed</span>
                <span><b class="text-red-300">{Number(counts().failed ?? 0) + Number(counts().dead_letter ?? 0)}</b> failed</span>
              </div>
            </div>
          </section>
        )}
      </Show>

      <Show when={view() !== "schedulers" && newUpdates() > 0}>
        <div class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-cyan-800 bg-cyan-950/35 px-4 py-3 text-sm text-cyan-100">
          <span>{newUpdates()} live update burst(s) arrived. This older cursor page remains stable.</span>
          <button onClick={reloadFromFirstPage} class="rounded-lg bg-cyan-700 px-3 py-2 font-semibold hover:bg-cyan-600">Show newest tasks</button>
        </div>
      </Show>

      <Show when={view() === "schedulers"}>
        <TaskQueueSchedulersPanel event={schedulerEvent} delta={schedulerDelta} bootstrapRevision={schedulerBootstrapRevision} eventCursor={schedulerEventCursor} queueNames={() => stats()?.queues.map((queue) => queue.name) ?? []} />
      </Show>

      <Show when={view() === "queue"}>
      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <div class="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 class="font-semibold text-gray-100">Queue health</h3>
            <p class="mt-1 text-sm text-gray-500">Select a status to filter the task list.</p>
          </div>
          <Show when={status()}>
            <button onClick={() => { setStatus(""); reloadFromFirstPage(); }} class="text-sm text-cyan-300 hover:text-cyan-200">Clear status filter</button>
          </Show>
        </div>
        <div class="mt-4 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-gray-800 bg-gray-800 sm:grid-cols-4 xl:grid-cols-8">
          <For each={STATUSES}>
            {(item) => (
              <button
                onClick={() => { setStatus(status() === item ? "" : item); reloadFromFirstPage(); }}
                class={`min-h-20 bg-gray-950 px-3 py-3 text-left transition ${status() === item ? "ring-1 ring-inset ring-cyan-500" : "hover:bg-gray-900"}`}
              >
                <div class="text-xs text-gray-500">{STATUS_LABELS[item]}</div>
                <div class="mt-1 text-xl font-semibold text-gray-100">{stats()?.statusCounts[item] ?? 0}</div>
              </button>
            )}
          </For>
        </div>
      </section>
      </Show>

      <Show when={view() === "queue" && settings()}>
        {(value) => (
          <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
            <QueueSectionHeading
              eyebrow="Worker policy"
              title="Execution defaults"
              description="Global limits used when a request or scheduler does not provide an explicit override. Coverage computation has its own settings."
              action={<button
                onClick={() => requestAction("Save task queue execution settings?", () => api.taskQueue.updateSettings(value()))}
                class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-950/30 hover:bg-cyan-500"
              >
                Save defaults
              </button>}
            />
            <div class="mt-5 grid gap-4 lg:grid-cols-3">
              <div class="rounded-xl border border-gray-800 bg-gradient-to-b from-gray-950/70 to-gray-950/40 p-4">
                <div class="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-500/70">On demand</div>
                <h4 class="mt-1 text-sm font-semibold text-gray-200">Request-triggered work</h4>
                <p class="mt-1 min-h-10 text-xs leading-5 text-gray-500">Limits opportunistic processing started after an API request.</p>
                <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <label class="text-sm text-gray-300">
                    Tasks per request
                    <input type="number" min="1" value={value().requestBatchSize} onInput={(event) => updateSetting("requestBatchSize", event.currentTarget.value)} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-gray-100" />
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Maximum tasks claimed by one request wake-up.</span>
                  </label>
                  <label class="text-sm text-gray-300">
                    Time budget
                    <div class="relative mt-1">
                      <input type="number" min="1" step="1" value={value().requestTimeBudgetMs / 1000} onInput={(event) => updateSetting("requestTimeBudgetMs", String(Number(event.currentTarget.value) * 1000))} class="block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-9 text-gray-100" />
                      <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">sec</span>
                    </div>
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Stops claiming new work when this runtime is reached.</span>
                  </label>
                </div>
              </div>

              <div class="rounded-xl border border-gray-800 bg-gradient-to-b from-gray-950/70 to-gray-950/40 p-4">
                <div class="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-500/70">Automated</div>
                <h4 class="mt-1 text-sm font-semibold text-gray-200">Scheduled work</h4>
                <p class="mt-1 min-h-10 text-xs leading-5 text-gray-500">Controls cron and VPS scheduler processing intended to drain queues.</p>
                <div class="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                  <label class="text-sm text-gray-300">
                    Tasks per run
                    <input type="number" min="1" value={value().scheduledBatchSize} onInput={(event) => updateSetting("scheduledBatchSize", event.currentTarget.value)} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-gray-100" />
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Maximum tasks claimed during one scheduled invocation.</span>
                  </label>
                  <label class="text-sm text-gray-300">
                    Time budget
                    <div class="relative mt-1">
                      <input type="number" min="1" step="1" value={value().scheduledTimeBudgetMs / 1000} onInput={(event) => updateSetting("scheduledTimeBudgetMs", String(Number(event.currentTarget.value) * 1000))} class="block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-9 text-gray-100" />
                      <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">sec</span>
                    </div>
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Bounds total work inside one scheduled invocation.</span>
                  </label>
                </div>
              </div>

              <div class="rounded-xl border border-gray-800 bg-gradient-to-b from-gray-950/70 to-gray-950/40 p-4">
                <div class="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-500/70">Failure handling</div>
                <h4 class="mt-1 text-sm font-semibold text-gray-200">Reliability</h4>
                <p class="mt-1 min-h-10 text-xs leading-5 text-gray-500">Defines ownership timeouts and exponential retry behavior.</p>
                <div class="mt-4 grid gap-4 sm:grid-cols-2">
                  <label class="text-sm text-gray-300">Lease duration
                    <div class="relative mt-1"><input type="number" min="1" value={value().leaseSeconds} onInput={(event) => updateSetting("leaseSeconds", event.currentTarget.value)} class="block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-9 text-gray-100" /><span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">sec</span></div>
                    <span class="mt-1 block text-xs leading-4 text-gray-600">A worker must finish or heartbeat before ownership expires.</span>
                  </label>
                  <label class="text-sm text-gray-300">Maximum attempts
                    <input type="number" min="1" value={value().maxAttempts} onInput={(event) => updateSetting("maxAttempts", event.currentTarget.value)} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-gray-100" />
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Failed tasks enter dead letter after this many tries.</span>
                  </label>
                  <label class="text-sm text-gray-300">Initial retry delay
                    <div class="relative mt-1"><input type="number" min="1" value={value().retryBaseSeconds} onInput={(event) => updateSetting("retryBaseSeconds", event.currentTarget.value)} class="block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-9 text-gray-100" /><span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">sec</span></div>
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Delay before the first retry; later delays grow exponentially.</span>
                  </label>
                  <label class="text-sm text-gray-300">Maximum retry delay
                    <div class="relative mt-1"><input type="number" min="1" value={value().retryMaxSeconds} onInput={(event) => updateSetting("retryMaxSeconds", event.currentTarget.value)} class="block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-9 text-gray-100" /><span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">sec</span></div>
                    <span class="mt-1 block text-xs leading-4 text-gray-600">Caps exponential backoff between attempts.</span>
                  </label>
                </div>
              </div>
            </div>
          </section>
        )}
      </Show>

      <Show when={view() === "coverage" && coverageSettings() && coverageQueueSettings()}>
        <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
          <QueueSectionHeading
            eyebrow="Coverage policy"
            title="Processing settings"
            description="Tune how coverage work is partitioned and how precise each materialized result should be. These settings apply only to the coverage queue."
            action={<button
              onClick={() => requestAction("Save coverage processing settings?", async () => {
                await api.taskQueue.updateCoverageSettings(coverageSettings()!);
                await api.taskQueue.updateQueueSettings("coverage", coverageQueueSettings()!);
              })}
              class="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-semibold text-white shadow-lg shadow-cyan-950/30 hover:bg-cyan-500"
            >Save coverage settings</button>}
          />
          <div class="mt-5 grid gap-4 xl:grid-cols-2">
            <div class="rounded-xl border border-gray-800 bg-gray-950/50 p-4">
              <div class="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-500/70">Work partitioning</div>
              <p class="mt-1 text-xs leading-5 text-gray-600">Safety limits determine when the planner starts a new durable task. The first reached limit wins.</p>
              <div class="mt-4 grid gap-4 sm:grid-cols-2">
                <CoverageSetting label="Maximum blocks" hint="Hard block count per task." value={coverageSettings()!.blocksPerBatch} onInput={(value) => updateCoverageSetting("blocksPerBatch", value)} />
                <CoverageSetting label="Cell budget" hint="Maximum estimated coverage cells per task." value={coverageSettings()!.cellsPerBatch} onInput={(value) => updateCoverageSetting("cellsPerBatch", value)} />
                <CoverageSetting label="Candidate sessions" hint="Upper safety cap before spatial filtering." value={coverageSettings()!.candidateSessionsPerBatch} onInput={(value) => updateCoverageSetting("candidateSessionsPerBatch", value)} />
                <CoverageSetting label="Task time budget" hint="Cooperative execution budget in milliseconds." value={coverageSettings()!.taskTimeBudgetMs} unit="ms" onInput={(value) => updateCoverageSetting("taskTimeBudgetMs", value)} />
                <CoverageSetting label="Queue concurrency" hint="Coverage tasks that may be leased concurrently." value={coverageQueueSettings()!.concurrencyLimit} onInput={(value) => setCoverageQueueSettings({ ...coverageQueueSettings()!, concurrencyLimit: Number(value) })} />
              </div>
            </div>
            <div class="rounded-xl border border-gray-800 bg-gray-950/50 p-4">
              <div class="text-xs font-semibold uppercase tracking-[0.14em] text-cyan-500/70">Coverage model</div>
              <p class="mt-1 text-xs leading-5 text-gray-600">These values affect cache lifetime, geometric precision, and the resulting utilization ratios.</p>
              <div class="mt-4 grid gap-4 sm:grid-cols-2">
                <CoverageSetting label="Freshness window" hint="Time before source changes are checked again." value={coverageSettings()!.freshnessSeconds} unit="sec" onInput={(value) => updateCoverageSetting("freshnessSeconds", value)} />
                <CoverageSetting label="Point radius" hint="Coverage radius stamped around each sample." value={coverageSettings()!.pointRadiusMeters} unit="m" onInput={(value) => updateCoverageSetting("pointRadiusMeters", value)} />
                <CoverageSetting label="Cell size" hint="Smaller cells improve precision but cost more work." value={coverageSettings()!.cellSizeMeters} unit="m" onInput={(value) => updateCoverageSetting("cellSizeMeters", value)} />
              </div>
            </div>
          </div>
        </section>
      </Show>

      <Show when={view() === "queue"}>
      <section class="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900">
        <div class="border-b border-gray-800 p-5">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="font-semibold text-gray-100">Task explorer</h3>
              <p class="mt-1 text-sm text-gray-500">Filter durable jobs and open a task to inspect its attempts.</p>
            </div>
            <div class="flex flex-wrap items-end gap-2">
              <Show when={queueName()}>
                <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Run limit
                  <input type="number" min="1" max="100" value={manualRunLimit()} onInput={(event) => setManualRunLimit(Number(event.currentTarget.value) || 1)} class="mt-1 w-20 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
                </label>
                <button onClick={() => requestProcessQueue(queueName())} class="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-500">Run queue</button>
              </Show>
              <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Minimum age
                <div class="relative mt-1">
                  <input type="number" min="0" value={failedTaskAgeDays()} onInput={(event) => setFailedTaskAgeDays(Math.max(0, Number(event.currentTarget.value) || 0))} class="w-28 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-12 text-sm font-normal normal-case text-gray-100" />
                  <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">days</span>
                </div>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Delete limit
                <input type="number" min="0" max="10000" value={terminalDeleteLimit()} onInput={(event) => setTerminalDeleteLimit(Math.max(0, Number(event.currentTarget.value) || 0))} class="mt-1 w-24 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal text-gray-100" title="Use 0 to delete all matching tasks" />
              </label>
              <div class="flex items-center gap-2 pb-2">
                <For each={["failed", "dead_letter", "cancelled"] as TaskQueueJobStatus[]}>
                  {(item) => <label class="flex items-center gap-1 text-xs text-gray-400"><input type="checkbox" checked={terminalDeleteStatuses().includes(item)} onChange={() => toggleTerminalDeleteStatus(item)} />{STATUS_LABELS[item]}</label>}
                </For>
              </div>
              <button
                onClick={() => requestTerminalTaskCleanup({
                  queueName: queueName() || undefined,
                  taskType: taskType() || undefined,
                  projectId: projectId() || undefined,
                })}
                class="rounded-lg border border-red-900 px-3 py-2 text-sm text-red-300 hover:bg-red-950/40"
              >
                Delete old terminal
              </button>
              <Show when={selectedQueue()}>
                {(queue) => (
                  <button
                    onClick={() => requestAction(
                      `${queue().paused ? "Resume" : "Pause"} queue "${queue().name}"?`,
                      () => queue().paused ? api.taskQueue.resume(queue().name) : api.taskQueue.pause(queue().name),
                    )}
                    class={`rounded-lg border px-3 py-2 text-sm ${queue().paused ? "border-emerald-800 text-emerald-300 hover:bg-emerald-950/40" : "border-amber-800 text-amber-300 hover:bg-amber-950/40"}`}
                  >
                    {queue().paused ? "Resume queue" : "Pause queue"}
                  </button>
                )}
              </Show>
            </div>
          </div>
          <div class="mt-4 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Queue
            <select value={queueName()} onChange={(event) => { setQueueName(event.currentTarget.value); reloadFromFirstPage(); }} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm normal-case text-gray-100">
              <option value="" selected={queueName() === ""}>All queues</option>
              <For each={(stats()?.queues ?? []).map((queue) => queue.name)}>
                {(name) => <option value={name} selected={queueName() === name}>{name}</option>}
              </For>
            </select>
          </label>
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Task type
            <input value={taskType()} onInput={(event) => setTaskType(event.currentTarget.value)} onChange={reloadFromFirstPage} placeholder="coverage.compute..." class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm normal-case text-gray-100" />
          </label>
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Project
            <input value={projectId()} onInput={(event) => setProjectId(event.currentTarget.value)} onChange={reloadFromFirstPage} placeholder="Project ID" class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm normal-case text-gray-100" />
          </label>
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            From
            <input type="datetime-local" value={from()} onChange={(event) => { setFrom(event.currentTarget.value); reloadFromFirstPage(); }} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100" />
          </label>
          <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
            To
            <input type="datetime-local" value={to()} onChange={(event) => { setTo(event.currentTarget.value); reloadFromFirstPage(); }} class="mt-1 block w-full rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100" />
          </label>
          </div>
        </div>

        <Show when={taskActionFeedback()}>
          {(feedback) => <div class={`mx-5 mt-4 flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${feedback().kind === "success" ? "border-emerald-800 bg-emerald-950/40 text-emerald-200" : "border-red-800 bg-red-950/40 text-red-200"}`}><span>{feedback().text}</span><button onClick={() => setTaskActionFeedback(null)} class="font-semibold">Close</button></div>}
        </Show>
        <div class="overflow-x-auto px-5">
          <table class="w-full text-left text-sm">
            <thead class="border-b border-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr><th class="px-3 py-2">Task</th><th>Status</th><th>Progress</th><th>Project</th><th>Attempts</th><th>Available</th><th class="text-right">Actions</th></tr>
            </thead>
            <tbody>
              <For each={tasks()}>
                {(task) => (
                  <tr class="border-b border-gray-800/70 text-gray-300">
                    <td class="px-3 py-3"><button class="text-left text-cyan-300 hover:underline" onClick={() => void api.taskQueue.task(task.id).then(setSelected).catch((cause) => setError(cause.message))}>{task.taskType}</button><div class="text-xs text-gray-600">{task.id}</div></td>
                    <td><span class={`rounded-full px-2 py-1 text-xs ${task.status === "leased" ? "bg-cyan-950 text-cyan-300" : "bg-gray-800"}`}>{STATUS_LABELS[task.status]}</span></td>
                    <td class="min-w-36">
                      {(() => { const progress = taskProgress(task); return <div><div class="flex justify-between text-xs text-gray-500"><span class="capitalize">{progress.phase}</span><Show when={progress.total > 0}><span>{progress.completed}/{progress.total}</span></Show></div><div class="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-800"><div class="h-full bg-cyan-500 transition-all" style={{ width: `${progress.percent}%` }} /></div></div>; })()}
                    </td>
                    <td>{task.projectId ?? "-"}</td>
                    <td>{task.attemptCount}/{task.maxAttempts}</td>
                    <td>{new Date(task.availableAt).toLocaleString()}</td>
                    <td class="space-x-2 text-right">
                      <Show when={["failed", "dead_letter", "cancelled"].includes(task.status)}>
                        <button onClick={() => requestAction("Retry this task?", () => api.taskQueue.retry(task.id))} class="text-cyan-300 hover:underline">Retry</button>
                      </Show>
                      <Show when={["pending", "retry_wait", "leased"].includes(task.status)}>
                        <button onClick={() => requestAction("Cancel this task?", () => api.taskQueue.cancel(task.id))} class="text-red-300 hover:underline">Cancel</button>
                      </Show>
                      <button disabled={["leased", "cancel_requested"].includes(task.status)} title={["leased", "cancel_requested"].includes(task.status) ? "Cancel this running task before deleting it." : "Permanently delete this task."} onClick={() => requestDeleteTask(task)} class="text-red-300 hover:underline disabled:cursor-not-allowed disabled:text-gray-600 disabled:no-underline">Delete</button>
                    </td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
          <Show when={!loading() && tasks().length === 0}><p class="py-10 text-center text-sm text-gray-500">No tasks match these filters.</p></Show>
        </div>
        <div class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-800 px-5 py-4">
          <p class="text-sm text-gray-500">
            <Show when={total() > 0} fallback="No tasks">
              {((page() - 1) * perPage()) + 1}-{Math.min(page() * perPage(), total())} of {total()} tasks
            </Show>
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-2 text-sm text-gray-400">
              Per page
              <select
                value={perPage()}
                onChange={(event) => {
                  setPerPage(Number(event.currentTarget.value));
                  reloadFromFirstPage();
                }}
                class="rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100"
              >
                <For each={PAGE_SIZES}>{(size) => <option value={size}>{size}</option>}</For>
              </select>
            </label>
            <span class="min-w-24 text-center text-sm text-gray-400">Page {page()} of {totalPages()}</span>
            <div class="flex overflow-hidden rounded-lg border border-gray-700">
              <button
                disabled={page() <= 1 || loading()}
                onClick={goToPreviousPage}
                class="border-r border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >Previous</button>
              <button
                disabled={!hasNext() || loading()}
                onClick={goToNextPage}
                class="px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        </div>
      </section>
      </Show>

      <Show when={view() === "coverage"}>
      <section class="rounded-2xl border border-gray-800 bg-gray-900 p-5">
        <h3 class="font-semibold text-gray-100">Coverage generations</h3>
        <p class="mt-1 text-sm text-gray-500">Recent project snapshots. Open a generation to isolate the durable queue work that builds it.</p>
        <div class="mt-4 space-y-2">
          <For each={coverageGenerations()}>
            {(generation) => (
              <div class="rounded-lg border border-gray-800 bg-gray-950 px-4 py-3 text-sm text-gray-300">
                <div class="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <span class="font-medium">{String(generation.projectId ?? "-")}</span>
                    <div class="mt-0.5 font-mono text-[11px] text-gray-600">{String(generation.id ?? "-")}</div>
                  </div>
                  <div class="flex items-center gap-2">
                    <span class="rounded-full bg-gray-800 px-2 py-1 text-xs">{String(generation.status ?? "-")}{generation.published ? " / published" : ""}</span>
                    <button
                      onClick={() => {
                        setGenerationId(String(generation.id ?? ""));
                        reloadFromFirstPage();
                      }}
                      class="rounded-lg border border-gray-700 px-2.5 py-1.5 text-xs text-cyan-300 hover:bg-gray-800"
                    >
                      View tasks
                    </button>
                    <Show when={["pending", "processing"].includes(String(generation.status ?? ""))}>
                      <button
                        onClick={() => requestAction(
                          `Cancel all unfinished coverage work for generation ${String(generation.id)}? Running work will be asked to stop safely.`,
                          () => api.taskQueue.cancelCoverage({
                            generationId: String(generation.id),
                            includeRunning: true,
                          }),
                        )}
                        class="rounded-lg border border-red-900 px-2.5 py-1.5 text-xs text-red-300 hover:bg-red-950/40"
                      >
                        Cancel generation
                      </button>
                    </Show>
                  </div>
                </div>
                <div class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                  <span>
                  {String(generation.completedBlocks ?? 0)} / {String(generation.totalBlocks ?? 0)} blocks
                  {Number(generation.inFlightBlocks ?? 0) > 0 ? ` + ${String(generation.inFlightBlocks)} currently processing` : ""}
                  {Number(generation.failedBlocks ?? 0) > 0 ? ` / ${String(generation.failedBlocks)} failed` : ""}
                  </span>
                  <span>{String(generation.runningTasks ?? 0)} running / {String(generation.pendingTasks ?? 0)} pending tasks</span>
                  <Show when={generation.createdAt}><span>Created {new Date(String(generation.createdAt)).toLocaleString()}</span></Show>
                  <Show when={generation.updatedAt}><span>Updated {new Date(String(generation.updatedAt)).toLocaleString()}</span></Show>
                </div>
                <div class="mt-2 h-2 overflow-hidden rounded-full bg-gray-800">
                  <div
                    class={`h-full transition-all duration-300 ${String(generation.status) === "failed" ? "bg-red-500" : "bg-cyan-500"}`}
                    style={{ width: `${Math.max(0, Math.min(100, Number(generation.totalBlocks ?? 0) > 0 ? (Number(generation.completedBlocks ?? 0) + Number(generation.inFlightBlocks ?? 0)) / Number(generation.totalBlocks) * 100 : 0))}%` }}
                  />
                </div>
              </div>
            )}
          </For>
          <Show when={coverageGenerations().length === 0}><p class="py-5 text-center text-sm text-gray-500">No coverage generations yet.</p></Show>
        </div>
      </section>
      </Show>

      <Show when={view() === "coverage"}>
      <section class="overflow-hidden rounded-2xl border border-gray-800 bg-gray-900">
        <div class="border-b border-gray-800 p-5">
          <div class="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 class="font-semibold text-gray-100">Coverage queue tasks</h3>
              <p class="mt-1 text-sm text-gray-500">Each task computes a known group of blocks for one coverage generation.</p>
            </div>
            <div class="flex flex-wrap gap-2">
              <label class="relative">
                <span class="sr-only">Coverage tasks to run</span>
                <input type="number" min="1" max="100" value={manualRunLimit()} onInput={(event) => setManualRunLimit(Number(event.currentTarget.value) || 1)} class="w-20 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm text-gray-100" />
              </label>
              <button onClick={() => requestProcessQueue("coverage")} disabled={coverageQueueSettings()?.paused} class="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white hover:bg-cyan-500 disabled:opacity-40">Run pending coverage</button>
              <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Minimum age
                <div class="relative mt-1"><input type="number" min="0" value={failedTaskAgeDays()} onInput={(event) => setFailedTaskAgeDays(Math.max(0, Number(event.currentTarget.value) || 0))} class="w-28 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 pr-12 text-sm font-normal text-gray-100" />
                <span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">days</span></div>
              </label>
              <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Delete limit
                <input type="number" min="0" max="10000" value={terminalDeleteLimit()} onInput={(event) => setTerminalDeleteLimit(Math.max(0, Number(event.currentTarget.value) || 0))} class="mt-1 block w-24 rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal text-gray-100" title="Use 0 to delete all matching tasks" />
              </label>
              <div class="flex items-center gap-2 pt-5">
                <For each={["failed", "dead_letter", "cancelled"] as TaskQueueJobStatus[]}>
                  {(item) => <label class="flex items-center gap-1 text-xs text-gray-400"><input type="checkbox" checked={terminalDeleteStatuses().includes(item)} onChange={() => toggleTerminalDeleteStatus(item)} />{STATUS_LABELS[item]}</label>}
                </For>
              </div>
              <button
                onClick={() => requestTerminalTaskCleanup({
                  queueName: "coverage",
                  projectId: projectId() || undefined,
                  generationId: generationId() || undefined,
                })}
                class="rounded-lg border border-red-900 px-3 py-2 text-sm text-red-300 hover:bg-red-950/40"
              >
                Delete old terminal
              </button>
              <button
                onClick={() => requestAction(
                  `Cancel all queued coverage work${generationId() ? ` for generation ${generationId()}` : projectId() ? ` for project ${projectId()}` : ""}? Running work will finish normally.`,
                  () => api.taskQueue.cancelCoverage({
                    projectId: projectId() || undefined,
                    generationId: generationId() || undefined,
                    includeRunning: false,
                  }),
                )}
                class="rounded-lg border border-red-900 px-3 py-2 text-sm text-red-300 hover:bg-red-950/40"
              >
                Cancel queued work
              </button>
              <Show when={coverageQueueSettings()}>
                {(queue) => (
                  <button
                    onClick={() => requestAction(
                      `${queue().paused ? "Resume" : "Pause"} the coverage queue?`,
                      () => queue().paused ? api.taskQueue.resume("coverage") : api.taskQueue.pause("coverage"),
                    )}
                    class={`rounded-lg border px-3 py-2 text-sm ${queue().paused ? "border-emerald-800 text-emerald-300" : "border-amber-800 text-amber-300"}`}
                  >
                    {queue().paused ? "Resume coverage queue" : "Pause coverage queue"}
                  </button>
                )}
              </Show>
            </div>
          </div>
          <div class="mt-4 flex flex-wrap items-end gap-3">
            <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Project
              <input value={projectId()} onInput={(event) => setProjectId(event.currentTarget.value)} onChange={reloadFromFirstPage} placeholder="All projects" class="mt-1 block rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100" />
            </label>
            <label class="text-xs font-semibold uppercase tracking-wide text-gray-500">
              Status
              <select value={status()} onChange={(event) => { setStatus(event.currentTarget.value as "" | TaskQueueJobStatus); reloadFromFirstPage(); }} class="mt-1 block rounded-lg border border-gray-700 bg-gray-950 px-3 py-2 text-sm font-normal normal-case text-gray-100">
                <option value="">All statuses</option>
                <For each={STATUSES}>{(item) => <option value={item}>{STATUS_LABELS[item]}</option>}</For>
              </select>
            </label>
            <Show when={generationId()}>
              <div class="rounded-lg border border-cyan-900 bg-cyan-950/30 px-3 py-2 text-xs text-cyan-200">
                Generation <span class="font-mono">{generationId()}</span>
                <button onClick={() => { setGenerationId(""); reloadFromFirstPage(); }} class="ml-3 font-semibold hover:text-white">Clear</button>
              </div>
            </Show>
          </div>
        </div>

        <Show when={taskActionFeedback()}>
          {(feedback) => <div class={`mx-5 mt-4 flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${feedback().kind === "success" ? "border-emerald-800 bg-emerald-950/40 text-emerald-200" : "border-red-800 bg-red-950/40 text-red-200"}`}><span>{feedback().text}</span><button onClick={() => setTaskActionFeedback(null)} class="font-semibold">Close</button></div>}
        </Show>
        <div class="overflow-x-auto px-5">
          <table class="w-full min-w-[900px] text-left text-sm">
            <thead class="border-b border-gray-800 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th class="px-3 py-3">Task</th>
                <th>Status</th>
                <th>Project</th>
                <th>Generation</th>
                <th>Blocks</th>
                <th>Progress</th>
                <th>Attempts</th>
                <th>Updated</th>
                <th class="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              <For each={tasks()}>
                {(task) => {
                  const meta = coverageTaskMeta(task);
                  return (
                    <tr class="border-b border-gray-800/70 text-gray-300">
                      <td class="px-3 py-3">
                        <button class="text-left text-cyan-300 hover:underline" onClick={() => void api.taskQueue.task(task.id).then(setSelected).catch((cause) => setError(cause.message))}>
                          {task.id}
                        </button>
                        <Show when={task.lastError}><div class="mt-1 max-w-xs truncate text-xs text-red-400" title={task.lastError ?? ""}>{task.lastError}</div></Show>
                      </td>
                      <td><span class="rounded-full bg-gray-800 px-2 py-1 text-xs">{STATUS_LABELS[task.status]}</span></td>
                      <td>{task.projectId ?? "-"}</td>
                      <td class="font-mono text-xs text-gray-500">{meta.generationId ?? "-"}</td>
                       <td>
                         {meta.isProjectTask
                           ? `${meta.blocksPerSlice || "Configured"} max / slice`
                           : meta.blockIds.length}
                       </td>
                      <td class="min-w-36">
                        {(() => { const progress = taskProgress(task); return <div><div class="flex justify-between text-xs text-gray-500"><span class="capitalize">{progress.phase}</span><Show when={progress.total > 0}><span>{progress.completed}/{progress.total}</span></Show></div><div class="mt-1 h-1.5 overflow-hidden rounded-full bg-gray-800"><div class="h-full bg-cyan-500 transition-all" style={{ width: `${progress.percent}%` }} /></div></div>; })()}
                      </td>
                      <td>{task.attemptCount}/{task.maxAttempts}</td>
                      <td class="text-xs text-gray-500">{new Date(task.updatedAt).toLocaleString()}</td>
                      <td class="space-x-2 text-right">
                        <Show when={task.status === "pending"}>
                          <button
                            disabled={coverageQueueSettings()?.paused}
                            title={coverageQueueSettings()?.paused ? "Resume the coverage queue before running this task." : "Run one bounded slice of this exact coverage task."}
                            onClick={() => requestAction(
                              `Run one bounded slice of coverage task ${task.id} in project ${task.projectId ?? "-"}?`,
                              () => api.taskQueue.processTask(task.id),
                            )}
                            class="text-cyan-300 hover:underline disabled:cursor-not-allowed disabled:text-gray-600 disabled:no-underline"
                          >Run slice</button>
                        </Show>
                        <Show when={["failed", "dead_letter", "cancelled"].includes(task.status)}>
                          <button onClick={() => requestAction(`Retry coverage task ${task.id}?`, () => api.taskQueue.retry(task.id))} class="text-cyan-300 hover:underline">Retry</button>
                        </Show>
                        <Show when={["pending", "retry_wait", "leased"].includes(task.status)}>
                          <button onClick={() => requestAction(`Cancel coverage task ${task.id}?`, () => api.taskQueue.cancel(task.id))} class="text-red-300 hover:underline">Cancel</button>
                        </Show>
                        <button disabled={["leased", "cancel_requested"].includes(task.status)} title={["leased", "cancel_requested"].includes(task.status) ? "Cancel this running task before deleting it." : "Permanently delete this task."} onClick={() => requestDeleteTask(task)} class="text-red-300 hover:underline disabled:cursor-not-allowed disabled:text-gray-600 disabled:no-underline">Delete</button>
                      </td>
                    </tr>
                  );
                }}
              </For>
            </tbody>
          </table>
          <Show when={!loading() && tasks().length === 0}><p class="py-10 text-center text-sm text-gray-500">No coverage tasks match these filters.</p></Show>
        </div>

        <div class="flex flex-wrap items-center justify-between gap-3 border-t border-gray-800 px-5 py-4">
          <p class="text-sm text-gray-500">
            <Show when={total() > 0} fallback="No tasks">
              {((page() - 1) * perPage()) + 1}-{Math.min(page() * perPage(), total())} of {total()} tasks
            </Show>
          </p>
          <div class="flex flex-wrap items-center gap-3">
            <label class="flex items-center gap-2 text-sm text-gray-400">
              Per page
              <select value={perPage()} onChange={(event) => { setPerPage(Number(event.currentTarget.value)); reloadFromFirstPage(); }} class="rounded-lg border border-gray-700 bg-gray-950 px-2 py-1.5 text-sm text-gray-100">
                <For each={PAGE_SIZES}>{(size) => <option value={size}>{size}</option>}</For>
              </select>
            </label>
            <span class="min-w-24 text-center text-sm text-gray-400">Page {page()} of {totalPages()}</span>
            <div class="flex overflow-hidden rounded-lg border border-gray-700">
              <button disabled={page() <= 1 || loading()} onClick={goToPreviousPage} class="border-r border-gray-700 px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40">Previous</button>
              <button disabled={!hasNext() || loading()} onClick={goToNextPage} class="px-3 py-1.5 text-sm text-gray-300 hover:bg-gray-800 disabled:opacity-40">Next</button>
            </div>
          </div>
        </div>
      </section>
      </Show>

      <Show when={selected()}>
        {(task) => (
          <div class="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setSelected(null)}>
            <div class="max-h-[85vh] w-full max-w-2xl overflow-auto rounded-xl border border-gray-700 bg-gray-900 p-5" onClick={(event) => event.stopPropagation()}>
              <div class="flex justify-between gap-4"><h3 class="font-semibold text-gray-100">{task().taskType}</h3><button onClick={() => setSelected(null)} class="text-gray-400">Close</button></div>
              <pre class="mt-4 overflow-auto rounded-lg bg-gray-950 p-4 text-xs text-gray-300">{JSON.stringify(task(), null, 2)}</pre>
            </div>
          </div>
        )}
      </Show>

      <Show when={confirmAction()}>
        {(action) => (
          <div class="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
            <div class="w-full max-w-md rounded-xl border border-gray-700 bg-gray-900 p-5 shadow-2xl">
              <h3 class="font-semibold text-gray-100">Confirm queue action</h3>
              <p class="mt-2 text-sm text-gray-400">{action().label}</p>
              <div class="mt-5 flex justify-end gap-2">
                <button onClick={() => setConfirmAction(null)} class="rounded-lg border border-gray-700 px-3 py-2 text-sm text-gray-200">Cancel</button>
                <button onClick={() => void executeConfirmed()} class="rounded-lg bg-cyan-600 px-3 py-2 text-sm font-semibold text-white">Confirm</button>
              </div>
            </div>
          </div>
        )}
      </Show>
    </div>
  );
}

function CoverageSetting(props: {
  label: string;
  hint: string;
  value: number;
  unit?: string;
  onInput: (value: string) => void;
}) {
  return (
    <label class="block min-w-0">
      <span class="text-sm font-medium text-gray-300">{props.label}</span>
      <div class="relative mt-1.5">
        <input
          type="number"
          min="0.25"
          value={props.value}
          onInput={(event) => props.onInput(event.currentTarget.value)}
          class={`h-10 w-full rounded-lg border border-gray-700 bg-gray-950 px-3 text-sm text-gray-100 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/15 ${props.unit ? "pr-12" : ""}`}
        />
        <Show when={props.unit}><span class="pointer-events-none absolute inset-y-0 right-3 flex items-center text-xs text-gray-500">{props.unit}</span></Show>
      </div>
      <span class="mt-1.5 block text-xs leading-5 text-gray-600">{props.hint}</span>
    </label>
  );
}
